import React, { useEffect, useState, useRef, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { FileText, Sparkles, MoreVertical, X, Loader2, Trash2, Info, Database, Copy } from 'lucide-react';
import { WidgetWrapper } from './WidgetWrapper';
import { Button } from '../ui/Button';
import { useAthena } from '../../hooks/useAthena';
interface TextWidgetProps {
  id: string;
  onRemove?: () => void;
  onDuplicate?: () => void;
  onParameterChange?: () => void;
  onDoubleClickHandle?: () => void;
  style?: React.CSSProperties;
  className?: string;
  onMouseDown?: React.MouseEventHandler;
  onMouseUp?: React.MouseEventHandler;
  onTouchEnd?: React.TouchEventHandler;
  initialPrompt?: string;
}
export const TextWidget = forwardRef<HTMLDivElement, TextWidgetProps>(({
  id,
  onRemove,
  onDuplicate,
  onParameterChange,
  onDoubleClickHandle,
  style,
  className,
  onMouseDown,
  onMouseUp,
  onTouchEnd,
  initialPrompt,
  ...props
}, ref) => {
  const {
    generateText
  } = useAthena();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('AI Generated Text');
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const hasAutoGeneratedRef = useRef(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const glassStyle: React.CSSProperties = {
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  const dropdownStyle: React.CSSProperties = {
    background: 'rgba(15, 23, 42, 0.55)',
    backdropFilter: 'blur(40px) saturate(180%)',
    WebkitBackdropFilter: 'blur(40px) saturate(180%)'
  };
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) && buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Focus edit input when edit mode opens
  useEffect(() => {
    if (editMode && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editMode]);
  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenerateError('');
    try {
      const systemPrompt = 'You are Athena, the energy trading intelligence engine for the Jua platform. Generate detailed, professional analytical text about weather patterns, forecast models, and energy generation. Be data-focused, precise, and use tables where appropriate.';
      const userPrompt = `Request: ${prompt}`;
      const generatedText = await generateText(systemPrompt, userPrompt);
      setContent(generatedText);
      setModalOpen(false);
      setGenerateError('');
      onParameterChange?.();
      // Generate a short descriptive title from the content
      try {
        const generatedTitle = await generateText('You generate very short titles (3-6 words max) that summarize analytical text. Return ONLY the title, no quotes, no punctuation at the end.', `Summarize this in 3-6 words as a title:\n\n${generatedText.slice(0, 300)}`);
        setTitle(generatedTitle.trim());
      } catch {
        // Fallback: use the prompt as title
        setTitle(prompt.length > 40 ? prompt.slice(0, 40) + '…' : prompt);
      }
    } catch (error) {
      console.error('Failed to generate text:', error);
      setGenerateError(error instanceof Error && error.message ? error.message : 'Failed to generate text. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };
  const handleEditWithAI = async () => {
    if (!editPrompt.trim() || isEditing) return;
    setIsEditing(true);
    try {
      const systemPrompt = 'You are Athena, the energy trading intelligence engine for the Jua platform. You will be given existing analytical text and an instruction on how to modify it. Apply the requested change while maintaining the professional tone, data focus, and overall structure. Return ONLY the updated text, no explanations.';
      const userPrompt = `Existing text:\n\n${content}\n\n---\n\nRequested change: ${editPrompt}`;
      const updatedText = await generateText(systemPrompt, userPrompt);
      setContent(updatedText);
      setEditMode(false);
      setEditPrompt('');
      onParameterChange?.();
      // Update title based on new content
      try {
        const generatedTitle = await generateText('You generate very short titles (3-6 words max) that summarize analytical text. Return ONLY the title, no quotes, no punctuation at the end.', `Summarize this in 3-6 words as a title:\n\n${updatedText.slice(0, 300)}`);
        setTitle(generatedTitle.trim());
      } catch {
        // keep existing title
      }
    } catch (error) {
      console.error('Failed to edit text:', error);
    } finally {
      setIsEditing(false);
    }
  };
  const renderContent = () => {
    if (content) {
      return <>
            <div className="relative flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] gap-2 min-w-0" style={glassStyle} data-id="element-1957">
              <div className="flex items-center gap-2 text-xs font-medium text-white/90" data-id="element-1958">
                <Sparkles size={14} className="text-[#EC4899]" data-id="element-1959" />
                {title}
              </div>
              <div className="relative" data-id="element-1960">
                <button ref={buttonRef} className="p-1.5 text-white/40 hover:text-white/80 transition-colors rounded-md hover:bg-white/[0.06]" onClick={() => setMenuOpen(!menuOpen)} data-id="element-1961">
                  <MoreVertical size={14} data-id="element-1962" />
                </button>
                {menuOpen && ReactDOM.createPortal(<div ref={menuRef} className="fixed w-44 rounded-xl shadow-2xl shadow-black/50 py-1 border border-white/[0.08] overflow-hidden z-[9999]" style={{
              top: (buttonRef.current?.getBoundingClientRect().bottom ?? 0) + 4,
              left: (buttonRef.current?.getBoundingClientRect().right ?? 176) - 176,
              ...dropdownStyle
            }} data-id="element-1963">
                      <div className="absolute inset-0 rounded-xl pointer-events-none" style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 50%, rgba(255,255,255,0.02) 100%)'
              }} data-id="element-1964" />
                      <button onClick={() => {
                setEditMode(true);
                setMenuOpen(false);
              }} className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] flex items-center gap-2" data-id="element-1965">
                        <Sparkles size={12} className="text-[#EC4899]" data-id="element-1966" /> Edit
                        with AI
                      </button>
                      <div className="h-px bg-white/[0.08] my-1" data-id="element-1967" />
                      <button className="relative w-full px-3 py-2 text-left text-xs text-slate-300 hover:bg-white/[0.06] flex items-center gap-2" onClick={() => {
                onDuplicate?.();
                setMenuOpen(false);
              }} data-id="element-1968">
                        <Copy size={12} data-id="element-1969" /> Duplicate
                      </button>
                      <button className="relative w-full px-3 py-2 text-left text-xs text-red-400 hover:bg-red-500/10 flex items-center gap-2" onClick={() => {
                onRemove?.();
                setMenuOpen(false);
              }} data-id="element-1970">
                        <Trash2 size={12} data-id="element-1971" /> Delete
                      </button>
                    </div>, document.body)}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4" data-id="element-1972">
              <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap" data-id="element-1973">
                {content}
              </p>
            </div>
            {editMode ? <div className="px-3 py-2.5 border-t border-white/[0.06] flex-shrink-0" style={glassStyle} data-id="element-1974">
                <div className="flex items-center gap-2" data-id="element-1975">
                  <div className="relative flex-1" data-id="element-1976">
                    <input ref={editInputRef} value={editPrompt} onChange={e => setEditPrompt(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !isEditing) handleEditWithAI();
                if (e.key === 'Escape') {
                  setEditMode(false);
                  setEditPrompt('');
                }
              }} placeholder="What do you want to change?" disabled={isEditing} className="w-full h-8 px-3 pr-8 rounded-lg bg-[#253348] border border-slate-600/40 text-xs text-white/90 placeholder:text-white/30 focus:outline-none focus:border-[#EC4899]/40 focus:ring-1 focus:ring-[#EC4899]/10 transition-all disabled:opacity-50" data-id="element-1977" />
                  </div>
                  <button onClick={handleEditWithAI} disabled={!editPrompt.trim() || isEditing} className="p-1.5 text-[#EC4899] hover:bg-[#EC4899]/15 rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0" data-id="element-1978">
                    {isEditing ? <Loader2 size={14} className="animate-spin" data-id="element-1979" /> : <Sparkles size={14} data-id="element-1980" />}
                  </button>
                  <button onClick={() => {
              setEditMode(false);
              setEditPrompt('');
            }} disabled={isEditing} className="p-1.5 text-white/30 hover:text-white/60 hover:bg-white/[0.06] rounded-md transition-colors flex-shrink-0 disabled:opacity-30" data-id="element-1981">
                    <X size={14} data-id="element-1982" />
                  </button>
                </div>
              </div> : <div className="px-4 py-2 flex items-center justify-between text-xs border-t border-white/[0.06]" style={glassStyle} data-id="element-1983">
                <div className="flex items-center gap-2 text-white/50" data-id="element-1984">
                  <Database size={12} data-id="element-1985" />
                  <span className="font-medium text-white/70" data-id="element-1986">AI Text</span>
                </div>
                <Info size={12} className="text-white/30" data-id="element-1987" />
              </div>}
          </>;
    }
    return <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-1988">
          <div className="w-full h-full border-2 border-dashed border-white/[0.08] rounded-xl flex flex-col items-center justify-center gap-4" data-id="element-1989">
            <div className="w-12 h-12 rounded-full bg-white/[0.04] flex items-center justify-center" data-id="element-1990">
              <FileText size={24} className="text-white/20" data-id="element-1991" />
            </div>
            <div className="space-y-1" data-id="element-1992">
              <p className="text-sm font-medium text-white/60" data-id="element-1993">
                No content yet
              </p>
              <p className="text-xs text-white/30" data-id="element-1994">
                Generate text analysis using AI
              </p>
            </div>
            <Button onClick={() => setModalOpen(true)} className="bg-[#EC4899] hover:bg-[#db2777] text-white border-none shadow-lg shadow-pink-900/20" data-id="element-1995">
              <Sparkles size={14} className="mr-2" data-id="element-1996" /> Create with AI
            </Button>
          </div>
        </div>;
  };
  const renderModal = () => {
    if (!modalOpen) return null;
    return ReactDOM.createPortal(<div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-1997">
          <div className="w-full max-w-lg bg-[#111827] border border-slate-700/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-1998">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.08]" data-id="element-1999">
              <div className="flex items-center gap-2" data-id="element-2000">
                <Sparkles size={18} className="text-[#EC4899]" data-id="element-2001" />
                <h2 className="text-lg font-semibold text-white" data-id="element-2002">
                  Generate Text with AI
                </h2>
              </div>
              <button onClick={() => {
            setModalOpen(false);
            setGenerateError('');
          }} className="text-white/40 hover:text-white transition-colors" data-id="element-2003">
                <X size={20} data-id="element-2004" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2005">
              <div className="space-y-2" data-id="element-2006">
                <label className="text-sm text-white/60" data-id="element-2007">
                  The text below should explain
                </label>
                <textarea value={prompt} onChange={e => {
              setPrompt(e.target.value);
              if (generateError) setGenerateError('');
            }} placeholder="e.g. Wind energy forecast trends for the selected region..." className="w-full h-24 bg-[#1E293B] border border-slate-700/50 rounded-lg p-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#EC4899]/50 resize-none" data-id="element-2008" />
              </div>
              {generateError && <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-lg bg-red-500/10 border border-red-500/20" data-id="element-2009">
                  <Info size={14} className="text-red-400 flex-shrink-0 mt-0.5" data-id="element-2010" />
                  <div className="space-y-1" data-id="element-2011">
                    <p className="text-xs font-medium text-red-400" data-id="element-2012">
                      Generation failed
                    </p>
                    <p className="text-[11px] text-red-400/70" data-id="element-2013">
                      {generateError}
                    </p>
                  </div>
                  <button onClick={() => setGenerateError('')} className="ml-auto p-0.5 text-red-400/50 hover:text-red-400 flex-shrink-0" data-id="element-2014">
                    <X size={12} data-id="element-2015" />
                  </button>
                </div>}
            </div>

            <div className="p-4 border-t border-white/[0.08] bg-[#1E293B]/50 flex justify-end gap-3" data-id="element-2016">
              <Button variant="ghost" onClick={() => {
            setModalOpen(false);
            setGenerateError('');
          }} className="text-white/60 hover:text-white" data-id="element-2017">
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={!prompt || isGenerating} className="bg-[#EC4899] hover:bg-[#db2777] text-white min-w-[100px]" data-id="element-2018">
                {isGenerating ? <>
                    <Loader2 size={14} className="mr-2 animate-spin" data-id="element-2019" />{' '}
                    Generating...
                  </> : <>
                    <Sparkles size={14} className="mr-2" data-id="element-2020" /> Generate
                  </>}
              </Button>
            </div>
          </div>
        </div>, document.body);
  };
  return <WidgetWrapper ref={ref} title="AI Text Analysis" onRemove={onRemove} onDoubleClickHandle={onDoubleClickHandle} style={style} className={className} onMouseDown={onMouseDown} onMouseUp={onMouseUp} onTouchEnd={onTouchEnd} {...props} data-id="element-2021">
        <div className="flex flex-col h-full bg-[#0B1120] relative jua-text-widget-body" data-id="element-2022">
          {renderContent()}
          {renderModal()}
        </div>
      </WidgetWrapper>;
});